<template>
  <div class="flex flex-col md:flex-row justify-between gap-y-4 gap-x-8 px-4 py-4 md:items-center rounded-lg bg-white">
    <div class="flex flex-row items-baseline gap-4 relative shrink-0">
      <img src="@/assets/icons/metro-bubbles.svg" class="h-10" alt="Bubbles">
      <span class="font-semibold text-lg bottom-1 relative">
        {{ program.poll_titles?.[poll.id] || poll.title || poll.name }}
      </span>
    </div>
    <div class="flex gap-3 items-center md:pr-4">
      <button
        v-for="entry in poll.entries"
        :key="entry.id"
        :disabled="submittingEntryId !== null"
        class="flex items-center justify-center rounded-xl bg-primary px-4 h-8 text-white whitespace-nowrap disabled:opacity-50"
        @click="submit(entry)"
      >
        <Loader2 v-if="submittingEntryId === entry.id" class="w-4 h-4 animate-spin" />
        <template v-else>
          {{ entry.label }}
        </template>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {ref} from "vue";
import {Loader2} from "@lucide/vue";
import {Poll, PollEntry, ProgramSnapshot} from "@/program/interfaces/Program";
import Consumer from "@/core/interfaces/consumers/Consumer";
import usePollSubmission from "@/program/composables/usePollSubmission";
import {pollEntryActions} from "@/core/http/resources/PollResource";

const props = defineProps<{
    /** The poll to display in place of the program hook. */
    poll: Poll;
    /** The program the poll belongs to. */
    program: ProgramSnapshot;
    /** The consumer answering the poll. */
    consumer: Consumer;
}>();

const emit = defineEmits(['answered', 'hidden']);

const {submitPollEntry} = usePollSubmission();

const submittingEntryId = ref<number | null>(null);

/**
 * Swap back to the regular hook as soon as possible: immediately for
 * entries without action, as soon as the action responds otherwise — the
 * answer recording is slow (it refreshes the program statuses) and
 * completes in the background. When the entry action creates a feedback
 * hiding the program, notify it as well.
 */
const submit = (entry: PollEntry) => {
    if (submittingEntryId.value !== null) {
        return;
    }

    if (pollEntryActions(entry).length) {
        submittingEntryId.value = entry.id;
    } else {
        emit('answered');
    }

    submitPollEntry(props.poll, entry, props.consumer, (responses) => {
        submittingEntryId.value = null;

        if (responses.some(response => (response as { hide_notification?: boolean })?.hide_notification)) {
            emit('hidden');
        }

        emit('answered');
    }).catch(() => {
        submittingEntryId.value = null;
    });
};
</script>
